import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from './components/StripeWebhookHandler';
import CustomerApp from './components/CustomerApp';
import AdminApp from './components/AdminApp';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import AuthPage from './components/AuthPage';
import OrderHistory from './components/OrderHistory';
import OrderTracking from './components/OrderTracking';
import ProfileManagement from './components/ProfileManagement';
import NotificationsPage from './components/NotificationsPage';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Cart from './components/Cart';
import CheckoutForm from './components/CheckoutForm';
import './App.css';

// Load Stripe
const stripePromise = getStripe();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleCartClick = () => {
    setShowCart(!showCart);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + (item.quantity || 0), 0);
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Elements stripe={stripePromise}>
        <Router>
          <Routes>
          <Route path="/login" element={<AuthPage isAdmin={false} />} />
          <Route path="/signup" element={<AuthPage isAdmin={false} />} />
          <Route path="/admin/login" element={<AuthPage isAdmin={true} />} />
          <Route path="/admin/signup" element={<AuthPage isAdmin={true} />} />
          
          <Route path="/" element={
            user ? (
              <Layout user={user} cartCount={getCartCount()} onCartClick={handleCartClick} isAdmin={false}>
                <CustomerApp 
                  cart={cart} 
                  setCart={setCart} 
                  showCart={showCart} 
                  setShowCart={setShowCart}
                  addToCart={addToCart}
                  removeFromCart={removeFromCart}
                  updateQuantity={updateQuantity}
                  getCartTotal={getCartTotal}
                />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/orders" element={
            user ? (
              <Layout user={user} cartCount={getCartCount()} onCartClick={handleCartClick} isAdmin={false}>
                <OrderHistory />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/track/:orderId" element={
            user ? (
              <Layout user={user} cartCount={getCartCount()} onCartClick={handleCartClick} isAdmin={false}>
                <OrderTracking />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/profile" element={
            user ? (
              <Layout user={user} cartCount={getCartCount()} onCartClick={handleCartClick} isAdmin={false}>
                <ProfileManagement user={user} isAdmin={false} />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/notifications" element={
            user ? (
              <Layout user={user} cartCount={getCartCount()} onCartClick={handleCartClick} isAdmin={false}>
                <NotificationsPage user={user} />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/admin" element={
            user ? (
              <Layout user={user} cartCount={0} onCartClick={() => {}} isAdmin={true}>
                <AdminDashboard user={user} />
              </Layout>
            ) : (
              <Navigate to="/admin/login" />
            )
          } />
          <Route path="/admin/orders" element={
            user ? (
              <Layout user={user} cartCount={0} onCartClick={() => {}} isAdmin={true}>
                <AdminApp user={user} showOrders={true} showProducts={false} />
              </Layout>
            ) : (
              <Navigate to="/admin/login" />
            )
          } />
          <Route path="/admin/products" element={
            user ? (
              <Layout user={user} cartCount={0} onCartClick={() => {}} isAdmin={true}>
                <AdminApp user={user} showOrders={false} showProducts={true} />
              </Layout>
            ) : (
              <Navigate to="/admin/login" />
            )
          } />
          <Route path="/admin/profile" element={
            user ? (
              <Layout user={user} cartCount={0} onCartClick={() => {}} isAdmin={true}>
                <ProfileManagement user={user} isAdmin={true} />
              </Layout>
            ) : (
              <Navigate to="/admin/login" />
            )
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Global Cart and Checkout */}
          {showCart && (
            <Cart
              cart={cart}
              onUpdateQuantity={updateQuantity}
              onRemove={removeFromCart}
              onCheckout={() => {
                setShowCart(false);
                setShowCheckout(true);
              }}
              onClose={() => setShowCart(false)}
              total={getCartTotal()}
            />
          )}

          {showCheckout && (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                cart={cart}
                total={getCartTotal()}
                onClose={() => setShowCheckout(false)}
                onSuccess={() => {
                  setCart([]);
                  setShowCheckout(false);
                  alert('Order placed successfully! You will receive a confirmation email.');
                }}
              />
            </Elements>
          )}
        </Router>
      </Elements>
    </ErrorBoundary>
  );
}

export default App;
